'use client';

/**
 * AppTopBar — bar atas aplikasi (port MGMX): hamburger sidebar + breadcrumb interaktif (kiri),
 * tanggal hari ini + menu user (kanan). Menu user: Ukuran tampilan (kepadatan) dan Logout.
 * Tinggi TETAP 48px di semua layar.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Calendar, ChevronDown, LogOut, Menu, Type } from 'lucide-react';
import BreadcrumbNav from '@/components/ui/BreadcrumbNav';
import { MENU_URL_MAP } from '@/lib/menuTree';
import { logoutWithConfirm } from '@/lib/logout';
import {
    DENSITY_LABEL, DENSITY_ORDER, effectiveDensity, loadDensity, saveDensity, type Density,
} from '@/lib/density';

interface AppTopBarProps {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
}

function formatTodayID() {
    const d = new Date();
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const months = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
    ];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function AppTopBar({ sidebarOpen, onToggleSidebar }: AppTopBarProps) {
    const pathname = usePathname();
    const [mounted, setMounted] = useState(false);
    const [today, setToday] = useState('');
    const [todayShort, setTodayShort] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [user, setUser] = useState<{ name?: string; username?: string } | null>(null);
    const [density, setDensityState] = useState<Density | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Nama menu aktif (untuk breadcrumb): URL menu terpanjang yang cocok dengan pathname.
    const menuName = useMemo(() => {
        if (!pathname) return null;
        let best: { name: string; len: number } | null = null;
        for (const [name, url] of Object.entries(MENU_URL_MAP)) {
            if (pathname === url || pathname.startsWith(url + '/')) {
                if (!best || url.length > best.len) best = { name, len: url.length };
            }
        }
        return best?.name ?? null;
    }, [pathname]);

    // Hydration-safe: nilai yang bergantung browser (tanggal, user, density) diisi setelah mount.
    useEffect(() => {
        setMounted(true);
        setToday(formatTodayID());
        setTodayShort(new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }));
        try {
            const raw = localStorage.getItem('user');
            if (raw) setUser(JSON.parse(raw));
        } catch { /* abaikan */ }
        setDensityState(loadDensity());
    }, []);

    // Terapkan ukuran tampilan ke <html data-density> → skala root font-size (globals.css).
    useEffect(() => {
        if (!mounted) return;
        document.documentElement.dataset.density = effectiveDensity(density);
        return () => { delete document.documentElement.dataset.density; };
    }, [density, mounted]);

    useEffect(() => {
        function onClickOutside(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const changeDensity = (d: Density) => {
        setDensityState(d);
        saveDensity(d);
    };

    const displayName = mounted ? (user?.name || user?.username || 'User') : 'User';
    const subLabel = mounted ? (user?.username || '') : '';
    const initial = displayName.charAt(0).toUpperCase();
    const effDensity: Density = mounted ? effectiveDensity(density) : 'normal';

    return (
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200 print:hidden">
            <div className="flex items-center justify-between gap-2 sm:gap-3 px-2 sm:px-4 xl:px-6 h-12">
                {/* Kiri: hamburger (kontrol sidebar tunggal) + breadcrumb */}
                <div className="flex items-center gap-1 min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={onToggleSidebar}
                        aria-label={sidebarOpen ? 'Tutup menu' : 'Buka menu'}
                        aria-expanded={sidebarOpen}
                        className="shrink-0 w-11 h-11 -ml-1 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200"
                    >
                        <Menu size={22} />
                    </button>
                    {menuName && <BreadcrumbNav menuName={menuName} />}
                </div>

                {/* Kanan: tanggal + user */}
                <div className="flex items-center gap-2 xl:gap-4 shrink-0">
                    {/* Tanggal: lengkap ≥1280, ringkas 768–1279, disembunyikan di HP */}
                    <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-600" title="Tanggal hari ini">
                        <Calendar size={14} className="text-slate-400" />
                        <span className="hidden xl:inline" suppressHydrationWarning>{today || ' '}</span>
                        <span className="xl:hidden" suppressHydrationWarning>{todayShort || ' '}</span>
                    </div>

                    <div className="w-px h-5 bg-slate-200 hidden md:block" />

                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            onClick={() => setMenuOpen(v => !v)}
                            className="flex items-center gap-2 px-2 py-1 min-h-(--touch-h) xl:min-h-0 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                            <div className="w-7 h-7 rounded-full bg-slate-800 text-white text-xs font-semibold flex items-center justify-center">
                                {initial}
                            </div>
                            <div className="hidden xl:flex flex-col items-start leading-tight">
                                <span className="text-xs font-semibold text-slate-800">{displayName}</span>
                                {subLabel && <span className="text-[10px] text-slate-500">{subLabel}</span>}
                            </div>
                            <ChevronDown size={12} className="text-slate-500" />
                        </button>

                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 w-60 max-w-[85vw] bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40">
                                <div className="px-3 py-2 border-b border-slate-100">
                                    <p className="text-xs font-semibold text-slate-800 truncate">{displayName}</p>
                                    {subLabel && <p className="text-[10px] text-slate-500 truncate">{subLabel}</p>}
                                </div>
                                {/* Ukuran tampilan (kepadatan) — preferensi per perangkat, 5 tingkat kecil → besar */}
                                <div className="px-3 py-2 border-b border-slate-100">
                                    <label htmlFor="density-select" className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                                        <Type size={12} /> Ukuran tampilan
                                    </label>
                                    <select
                                        id="density-select"
                                        value={effDensity}
                                        onChange={(e) => changeDensity(e.target.value as Density)}
                                        className="w-full border border-slate-200 rounded-md px-2 py-1.5 pointer-coarse:min-h-(--touch-h) text-xs bg-white text-slate-700 focus:outline-none focus:border-slate-400"
                                    >
                                        {DENSITY_ORDER.map((d) => (
                                            <option key={d} value={d}>{DENSITY_LABEL[d]}</option>
                                        ))}
                                    </select>
                                    {density === null && (
                                        <p className="mt-1 text-[10px] text-slate-400">Saat ini otomatis menurut perangkat.</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={logoutWithConfirm}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 pointer-coarse:py-3 text-xs text-red-600 hover:bg-red-50"
                                >
                                    <LogOut size={13} /> Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
